import AIGradientIcon from 'assets/svg/icons/AIGradientIcon';
import classNames from 'classnames';
import Button from 'components/atoms/Button/Button.component';
import { AnimatePresence, motion } from 'framer-motion';
import useGetCurrentCustomerData from 'hooks/Authentication/useGetCurrentCustomerData';
import useUpdateDocument from 'hooks/DocumentsRepository/useUpdateDocument';
import useUpdateDocumentField from 'hooks/DocumentsRepository/useUpdateDocumentField';
import useUpdateUserDocumentField from 'hooks/DocumentsRepository/useUpdateUserDocumentField';
import { useState } from 'react';
import type { TFunction } from 'react-i18next';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import type {
  DocumentReferenceField,
  DocumentReferenceFieldTax,
  DocumentReferenceFieldUser,
} from 'types/repository.types';
import type { IUser } from 'types/users.types';
import formatPeriodLabel from 'utils/formatPeriodLabel';
import {
  CheckIconOutline,
  ChevronDownIconOutline,
  XIconOutline,
} from 'utils/icons';
import useDocumentFields from './useDocumentFields';
import useTaxLink from './useTaxLink';

type DocumentRepositoryFieldsProps = {
  documentId: string;
};

const formatTextVariables = (
  t: TFunction,
  field: DocumentReferenceField,
  user: IUser,
) => {
  if (field.referenceType === 'tax') {
    return {
      taxType: t(`taxes.filters.types.${field.value.type}`),
      periodString: formatPeriodLabel(field.value.period),
    };
  }
  if (field.type === 'VATRegime') {
    return {
      oldVATType: user.VATType,
      value: field.value,
    };
  }
  return { value: field.value };
};

type FieldItemProps = {
  field: DocumentReferenceField;
  onAccept: (field: DocumentReferenceField) => void;
  onReject: (field: DocumentReferenceField) => void;
  isLoading: boolean;
};

const FieldItem = ({
  field,
  onAccept,
  onReject,
  isLoading,
}: FieldItemProps) => {
  const { t } = useTranslation();

  const user = useGetCurrentCustomerData().data as IUser;

  const handleButtonClick = (e: React.MouseEvent, callback: () => void) => {
    e.stopPropagation();
    callback();
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.3 }}
      className={
        'flex flex-col gap-1 self-center border border-primary-100 shadow-[0px_1px_3px_0px_rgba(27,27,27,0.05)] rounded-lg p-3 shadow-[0px_1px_2px_0px_rgba(27,27,27,0.10),0px_0px_0px_1px_#E7E7E7]'
      }
      onClick={(e) => e.stopPropagation()}
    >
      <ReactMarkdown className="text-sm font-semibold">
        {t(`repository.document_form.fields.${field.type}.title`, {
          ...formatTextVariables(t, field, user),
        })}
      </ReactMarkdown>

      <ReactMarkdown className="text-sm">
        {t(`repository.document_form.fields.${field.type}.description`, {
          ...formatTextVariables(t, field, user),
        })}
      </ReactMarkdown>

      <div className="flex gap-2 mt-2">
        <Button
          onClick={(e) => handleButtonClick(e, () => onReject(field))}
          structure="light"
          className="flex-1 [&>svg]:!w-5 [&>svg]:!h-5"
          size="xs"
          icon={XIconOutline}
          disabled={isLoading}
          loading={isLoading}
        >
          {t('repository.document_form.fields.reject')}
        </Button>
        <Button
          onClick={(e) => handleButtonClick(e, () => onAccept(field))}
          structure="light"
          className="flex-1 [&>svg]:!w-5 [&>svg]:!h-5"
          color="taxes"
          size="xs"
          icon={CheckIconOutline}
          disabled={isLoading}
          loading={isLoading}
        >
          {t('repository.document_form.fields.accept')}
        </Button>
      </div>
    </motion.div>
  );
};

const DocumentRepositoryTaxField = ({
  field,
  documentId,
}: {
  field: DocumentReferenceField & DocumentReferenceFieldTax;
  documentId: string;
}) => {
  const { mutate: updateDocument, isLoading: isUpdateDocumentLoading } =
    useUpdateDocument();
  const {
    mutate: updateDocumentField,
    isLoading: isUpdateDocumentFieldLoading,
  } = useUpdateDocumentField(documentId);
  const { taxId } = useTaxLink(field);

  const isLoading = isUpdateDocumentLoading || isUpdateDocumentFieldLoading;

  const handleAccept = () => {
    if (!taxId) return;

    updateDocument({ documentId, taxId });
    updateDocumentField({ ...field, status: 'accepted' });
  };

  const handleReject = () =>
    updateDocumentField({ ...field, status: 'rejected' });

  if (!taxId) return null;

  return (
    <FieldItem
      field={field}
      onAccept={handleAccept}
      onReject={handleReject}
      isLoading={isLoading}
    />
  );
};

const DocumentRepositoryUserField = ({
  field,
  documentId,
}: {
  field: DocumentReferenceField & DocumentReferenceFieldUser;
  documentId: string;
}) => {
  const {
    mutate: updateUserDocumentField,
    isLoading: isUpdateUserDocumentLoading,
  } = useUpdateUserDocumentField();

  const {
    mutate: updateDocumentField,
    isLoading: isUpdateDocumentFieldLoading,
  } = useUpdateDocumentField(documentId);

  const isLoading = isUpdateUserDocumentLoading || isUpdateDocumentFieldLoading;

  const handleAccept = () => {
    updateUserDocumentField({ [field.type]: field.value });
    updateDocumentField({ ...field, status: 'accepted' });
  };

  const handleReject = () =>
    updateDocumentField({ ...field, status: 'rejected' });

  return (
    <FieldItem
      field={field}
      onAccept={handleAccept}
      onReject={handleReject}
      isLoading={isLoading}
    />
  );
};

const useFieldsFilter = (fields: DocumentReferenceField[] = []) => {
  const user = useGetCurrentCustomerData().data as IUser;

  // there can be only one tax field
  const taxField = fields?.find((field) => field.referenceType === 'tax');
  const { taxId } = useTaxLink(taxField as DocumentReferenceFieldTax);

  return fields?.filter((field) => {
    if (['accepted', 'rejected'].includes(field.status || '')) return false;

    if (field.type === 'tax') return !!taxId;

    // TODO: re-enable VATRegime when we implement the confirm dialog
    if (field.type === 'VATRegime') return field.status === 'pending';

    // only show fields that are not the same as the user's
    if (field.type === 'VATNumber') return user.VATNumber !== field.value;
    if (field.type === 'VATReturnFrequency')
      return user.VATReturnFrequency !== field.value;
    if (field.type === 'steuernummer') return user.steuernummer !== field.value;

    return true;
  });
};

const DocumentRepositoryFields = ({
  documentId,
}: DocumentRepositoryFieldsProps): JSX.Element | null => {
  const { data: documentFields } = useDocumentFields(documentId);

  const [isOpen, setIsOpen] = useState(false);

  const fields = useFieldsFilter(documentFields);

  if (!fields?.length) return null;

  return (
    <div
      className={classNames(
        'flex flex-col rounded-lg transition-all duration-300',
        !isOpen && 'ai-border-animated max-h-[44px]',
        isOpen && 'ai-border max-h-[800px]',
      )}
      onClick={() => setIsOpen(!isOpen)}
    >
      <div className="flex gap-2 p-2.5 rounded-lg items-center">
        <div className="flex items-center justify-center">
          <AIGradientIcon className="w-6 h-6" />
        </div>

        <h4 className="flex-1 text-sm">AI Suggestions</h4>

        <div className="flex items-center justify-center pt-0.5">
          <ChevronDownIconOutline className="w-5 h-5" />
        </div>
      </div>

      {isOpen && (
        <div className="flex flex-col gap-2 px-4 py-1 mb-2">
          <AnimatePresence>
            {fields?.map((field) =>
              field.referenceType === 'tax' ? (
                <DocumentRepositoryTaxField
                  key={field._id}
                  field={field}
                  documentId={documentId}
                />
              ) : (
                <DocumentRepositoryUserField
                  key={field._id}
                  field={field}
                  documentId={documentId}
                />
              ),
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};

export default DocumentRepositoryFields;
